import type { RendererProps } from "../core/element-registry";
import type { CogoElement } from "../core/types";
import { useRenderContext } from "../core/render-context";
import { useAtomDefaults } from "../contexts/atom-defaults-context";
import { ElementRenderer, liftV4Props } from "./element-renderer";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
  CardContent,
  CardFooter,
} from "../components/ui/card";
import { cn } from "../lib/utils";
import { stripPresetConflicts } from "../lib/preset-utils";
import { paddingClasses, spacingToken } from "../lib/design-tokens";
import {
  ParentActivationProxy,
  parentActivationHostStyle,
  PARENT_ACTIVATION_HOST_CLASSES,
  resolveParentActivationLabel,
} from "./parent-activation";

export function CardRenderer({ element }: RendererProps) {
  const { dispatchEvent, hasEventBinding, itemContext, resolveSymbol } =
    useRenderContext();
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("card");
  const resolvedVariant =
    element.variant ?? atomDefaults.defaultVariant ?? "default";
  const resolvedSize =
    (element.size as string | undefined) ??
    atomDefaults.defaultSize ??
    "default";
  const atomDataAttrs = {
    "data-atom-type": "card" as const,
    "data-variant": resolvedVariant,
    "data-size": resolvedSize,
    "data-padding":
      element.padding == null
        ? undefined
        : serializeCardPadding(element.padding),
  };
  const containerVariant = element.variant ?? "";

  const variantClasses: Record<string, string> = {
    card: "overflow-hidden transition-shadow hover:shadow-lg",
    default: "",
  };

  // Resolve tap event → onClick (same pattern as container-renderer)
  const tapHandler = element.events?.tap;
  const hasTap =
    Boolean(tapHandler) || (hasEventBinding?.(element.id, "tap") ?? false);
  const isNavigateAction =
    typeof tapHandler === "object" &&
    tapHandler.action === "navigate" &&
    tapHandler.params;
  const navigateRoute = isNavigateAction
    ? String(resolveSymbol(tapHandler.params!.route as string) ?? "")
    : undefined;
  const usesActivationProxy = hasTap;

  const handleClick = hasTap
    ? (e: React.MouseEvent) => {
        if (isNavigateAction && navigateRoute) {
          e.preventDefault();
          dispatchEvent(element.id, "tap", {
            __resolvedRoute: navigateRoute,
            ...itemContext,
          });
        } else {
          dispatchEvent(element.id, "tap", itemContext);
        }
      }
    : undefined;
  const resolvedAriaLabel = element.ariaLabel
    ? String(resolveSymbol(element.ariaLabel) ?? "")
    : undefined;
  const activationLabel = resolveParentActivationLabel(element, resolveSymbol);
  const sourceStyle = element.style as React.CSSProperties | undefined;
  const hostStyle = usesActivationProxy
    ? parentActivationHostStyle(element, sourceStyle)
    : sourceStyle;
  const cardStyle = element.padding ? { ...hostStyle, padding: 0 } : hostStyle;
  const contentClasses = element.padding
    ? cn(
        "flex flex-col gap-[var(--cogo-surface-gap)]",
        paddingClasses(element.padding),
      )
    : undefined;

  // Helper: render a card child based on its semantic type. liftV4Props
  // mirrors normalizeElement's first pass so v4 schema (`{props:{text}}`)
  // surfaces `child.text` for the slot-text branches below.
  function renderCardChild(rawChild: CogoElement) {
    const child = liftV4Props(rawChild);
    switch (child.type) {
      case "card-header":
        return (
          <CardHeader
            key={child.id}
            className={child.className}
            data-cogo-element-id={child.id}
            data-cogo-type={child.type}
          >
            {child.children?.map((c) => renderCardChild(c))}
          </CardHeader>
        );
      case "card-title": {
        const resolvedText = child.text
          ? String(resolveSymbol(child.text) ?? child.text)
          : undefined;
        const rawLevel = (child as unknown as { level?: unknown }).level;
        const resolvedLevel =
          typeof rawLevel === "string" ? resolveSymbol(rawLevel) : rawLevel;
        const numericLevel = Number(resolvedLevel);
        const levelTag = Number.isFinite(numericLevel)
          ? (`h${Math.min(6, Math.max(1, Math.floor(numericLevel)))}` as
              | "h1"
              | "h2"
              | "h3"
              | "h4"
              | "h5"
              | "h6")
          : undefined;
        // `level` is the semantic ontology contract. Keep the historical
        // explicit `tag` path as a fallback when no level is authored, and
        // preserve CardTitle's div default when neither is present.
        const authoredTag = (child as { tag?: string }).tag as
          | "div"
          | "h1"
          | "h2"
          | "h3"
          | "h4"
          | "h5"
          | "h6"
          | undefined;
        const headingTag = levelTag ?? authoredTag;
        return (
          <CardTitle
            key={child.id}
            className={child.className}
            tag={headingTag}
            data-cogo-element-id={child.id}
            data-cogo-type={child.type}
          >
            {resolvedText}
            {child.children?.map((c) => (
              <ElementRenderer key={c.id} element={c} />
            ))}
          </CardTitle>
        );
      }
      case "card-description": {
        const resolvedText = child.text
          ? String(resolveSymbol(child.text) ?? child.text)
          : undefined;
        return (
          <CardDescription
            key={child.id}
            className={child.className}
            data-cogo-element-id={child.id}
            data-cogo-type={child.type}
          >
            {resolvedText}
            {child.children?.map((c) => (
              <ElementRenderer key={c.id} element={c} />
            ))}
          </CardDescription>
        );
      }
      case "card-action":
        return (
          <CardAction
            key={child.id}
            className={child.className}
            data-cogo-element-id={child.id}
            data-cogo-type={child.type}
          >
            {child.children?.map((c) => (
              <ElementRenderer key={c.id} element={c} />
            ))}
          </CardAction>
        );
      case "card-content":
        const contentText = child.text
          ? String(resolveSymbol(child.text) ?? child.text)
          : undefined;
        return (
          <CardContent
            key={child.id}
            className={child.className}
            data-cogo-element-id={child.id}
            data-cogo-type={child.type}
          >
            {contentText}
            {child.children?.map((c) => (
              <ElementRenderer key={c.id} element={c} />
            ))}
          </CardContent>
        );
      case "card-footer":
        return (
          <CardFooter
            key={child.id}
            className={child.className}
            data-cogo-element-id={child.id}
            data-cogo-type={child.type}
          >
            {child.children?.map((c) => (
              <ElementRenderer key={c.id} element={c} />
            ))}
          </CardFooter>
        );
      default:
        // Fallback: render non-compound children via ElementRenderer
        return <ElementRenderer key={child.id} element={child} />;
    }
  }

  // Render children using compound-aware helper
  const renderChildren = () =>
    element.children?.map((child) => renderCardChild(child));
  const renderPaddedChildren = () =>
    element.padding != null ? (
      <div
        className={contentClasses}
        style={{ padding: resolveCardPaddingStyle(element.padding) }}
        data-padding={serializeCardPadding(element.padding)}
      >
        {renderChildren()}
      </div>
    ) : (
      <>{renderChildren()}</>
    );

  // Render as <a> for navigate actions
  if (isNavigateAction && navigateRoute) {
    if (usesActivationProxy) {
      return (
        <div
          aria-label={resolvedAriaLabel}
          className={cn(
            variantClasses[containerVariant] ?? "",
            stripPresetConflicts(element.className, undefined, element.designAuthorityClassName),
            PARENT_ACTIVATION_HOST_CLASSES,
          )}
          style={hostStyle}
          data-cogo-composite-action-host="true"
          {...atomDataAttrs}
        >
          <ParentActivationProxy
            elementId={element.id}
            href={navigateRoute}
            label={activationLabel}
            onClick={handleClick!}
          />
          {renderPaddedChildren()}
        </div>
      );
    }
    return (
      <a
        href={navigateRoute}
        aria-label={resolvedAriaLabel}
        className={cn(
          variantClasses[containerVariant] ?? "",
          stripPresetConflicts(element.className, undefined, element.designAuthorityClassName),
        )}
        style={sourceStyle}
        onClick={handleClick}
        {...atomDataAttrs}
      >
        {renderPaddedChildren()}
      </a>
    );
  }

  return (
    <Card
      className={cn(
        variantClasses[containerVariant] ?? "",
        stripPresetConflicts(element.className, undefined, element.designAuthorityClassName),
        usesActivationProxy && PARENT_ACTIVATION_HOST_CLASSES,
      )}
      size={(element.size as "default" | "sm") ?? "default"}
      onClick={usesActivationProxy ? undefined : handleClick}
      role={
        usesActivationProxy
          ? undefined
          : hasTap
            ? (element.role ?? "button")
            : element.role
      }
      tabIndex={
        usesActivationProxy
          ? undefined
          : hasTap
            ? (element.tabIndex ?? 0)
            : element.tabIndex
      }
      aria-label={resolvedAriaLabel}
      style={cardStyle}
      data-cogo-composite-action-host={usesActivationProxy ? "true" : undefined}
      {...atomDataAttrs}
    >
      {usesActivationProxy && (
        <ParentActivationProxy
          elementId={element.id}
          label={activationLabel}
          onClick={handleClick!}
        />
      )}
      {renderPaddedChildren()}
    </Card>
  );
}

function resolveCardPaddingStyle(padding: string | number): string {
  const token = spacingToken(padding);
  const numeric = Number(token);
  if (Number.isFinite(numeric)) return `${numeric * 4}px`;
  return String(padding);
}

function serializeCardPadding(padding: string | number): string {
  return String(padding);
}
